import { useCallback, useEffect, useRef, useState } from 'react';
import { isObject } from 'underscore';
import { ICourse, ICourseCertificate } from '@/types/courses';
import { CreateCourseCertificate, UpdateCourseCertificate } from '@/connection/courses';
import { ICONS } from '@ui/icons/types';
import { useAppSelector } from '@/store/hooks';
import { getUser } from '@/store/slices/user/selectors';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import Icon from '@ui/icons/icon';
import toastTrigger from '@ui/toast/toast-trigger';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import OptionalComponent from '@ui/optional-component/optional-component';
import UploadLarge from '@ui/uploads/upload-large';
import { GetUploadEndpoint, getUploadFilename, UploadFileToProvider } from '@ui/uploads/validations';
import handleImageUploadErrors from '@ui/image-upload/handle-image-upload-errors';
import { useCertificateDesign } from '../hooks/use-certificate-design';
import { GenerateMockCertificate, MockCertificate2 } from '../course-certificate/mock-certificate';
import CanvasComponent from '../course-certificate/canvas-component';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import { generateBorderDashSvg } from '@/micros/edit-project/helpers/constants/border-constants';
import useGetColorForProjectThemeComponents from '@/utils/colors/use-get-color-for-project-theme-components';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';

type CourseEditCertificateModalProps = {
  open: boolean;
  onComplete: (updatedCert?: ICourseCertificate) => void;
  course: ICourse | null;
};

function CourseEditCertificateModal({ open, course, onComplete }: CourseEditCertificateModalProps) {
  const user = useAppSelector(getUser);

  const activeChannel = user?.active_channel;
  const userId = user?.id;

  const { hex } = useGetColorForProjectThemeComponents({
    colorVariable: EColorVariablesV2.primary,
    contrastAgainstAppBackground: false,
  });

  const {
    loading,
    certificateDesigns,
    selectedCertificateDesign,
    selectedCertificate,
    setSelectedCertificateDesign,
    setSelectedCertificate,
  } = useCertificateDesign({ activeChannel, course, userId });

  const [blinkCanvas, setBlinkCanvas] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const hasSelectedCertificate = useRef(!!selectedCertificateDesign);
  const editImageRef = useRef<HTMLInputElement | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);
  const brandliveLogo = useRef('https://assets.brandlive.com/assets/emails/invitation/brandlive_logo_grey.png');

  useEffect(() => {
    hasSelectedCertificate.current = !!selectedCertificateDesign;
  }, [selectedCertificateDesign]);

  const handleFile = async (file: File | FileList) => {
    try {
      if (file instanceof File) {
        setUploading(true);

        const endpoint = await GetUploadEndpoint({
          filename: getUploadFilename(file.name),
        });

        if (isObject(endpoint) && endpoint.url) {
          const finalUrl = await UploadFileToProvider(
            {
              url: endpoint.url,
              fields: endpoint.fields,
              cdnHost: endpoint?.cdnHost,
            },
            file
          );

          setSelectedCertificate((prev) => {
            if (!prev) {
              return prev;
            } else {
              return { ...prev, settings: { ...prev.settings, icon: finalUrl } };
            }
          });
        }
      }
    } catch (error) {
      console.error(error);
      handleImageUploadErrors(error);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = useCallback(async () => {
    if (!selectedCertificate || !activeChannel || !selectedCertificateDesign || saving) {
      return;
    }

    try {
      setSaving(true);

      let cert: ICourseCertificate;

      if (!('uuid' in selectedCertificate)) {
        cert = await CreateCourseCertificate(activeChannel, {
          ...selectedCertificate,
          design_id: selectedCertificateDesign.id,
        });
      } else {
        cert = await UpdateCourseCertificate(activeChannel, selectedCertificate.uuid, {
          ...selectedCertificate,
          design_id: selectedCertificateDesign.id,
        });
      }

      onComplete(cert);
    } catch (e) {
      console.error(e);
      toastTrigger(<div className="S1">Failed to save certificate</div>, {
        type: 'error',
        autoClose: 10000,
      });
    } finally {
      setSaving(false);
    }
  }, [selectedCertificate, activeChannel, selectedCertificateDesign, saving, onComplete]);

  return (
    <Modal
      open={open}
      fadeTransition={false}
      transitionDuration={400}
      size="full"
      withZoom={false}
      backgroundColor="var(--gray-1000)"
      showBackdrop={false}
      delayContentRender={150}
      renderHeaderContent={() => (
        <Container
          background="var(--gray-800)"
          width="100%"
          className="flex fd-row jc-space-between ai-center padding-16"
          margin="0 auto"
        >
          <div className="flex ai-center gap-24">
            <img
              src={brandliveLogo.current}
              alt="BrandLive"
              width="56px"
              style={{ maxWidth: '56px', display: 'block', border: '0' }}
            />
            <Container
              background="var(--gray-900)"
              borderColor="var(--gray-700)"
              borderStyle="solid"
              borderWidth="1px"
              height="40px"
            />
            <Text className="H5" color="var(--gray-200)">
              Edit certificate design
            </Text>
          </div>
          <Container className="flex fd-row jc-flex-start ai-center gap-16">
            <Button design="LocalBttnSecHalfTransp" designSize="medium" onClick={() => onComplete()} disabled={saving}>
              <Text className="L1">Cancel</Text>
            </Button>
            <Button
              backgroundColor={getThemeColorCSSVariable()}
              hoverBackgroundColor={getThemeColorsCssMixVariable()}
              designSize="medium"
              loading={saving}
              disabled={saving}
              onClick={handleSave}
            >
              <Text className="L1" color={getThemeColorContrastCSSVariable()}>
                Save
              </Text>
            </Button>
          </Container>
        </Container>
      )}
    >
      <div className="flex height-100-percent">
        <Container minWidth="400px" height="100%" background="var(--gray-900)" className="flex-column padding-24">
          {loading && <WaitingIndicator />}
          {!loading && course && (
            <Text className="H5" color="var(--gray-100)">
              {course.name.base || 'Course'}
            </Text>
          )}
          {!loading && selectedCertificate && (
            <div className="flex-column gap-8 padding-t-24">
              <Container className="padding-16 radius-12 flex-column gap-10" background="var(--gray-800)">
                <Text className="S1" color="var(--gray-100)">
                  Logo
                </Text>
                {selectedCertificate.settings?.icon ? (
                  <Container
                    className="flex ai-center gap-10"
                    borderRadius="12px"
                    background="var(--gray-700)"
                    padding="8px"
                    maxWidth="320px"
                  >
                    <Container
                      className="flex ai-center jc-center"
                      height="48px"
                      width="86px"
                      padding="12px"
                      borderRadius="8px"
                      background="var(--gray-600)"
                      loading={uploading}
                    >
                      <OptionalComponent display={!uploading}>
                        <img
                          src={selectedCertificate.settings.icon}
                          alt="Logo"
                          style={{
                            objectFit: 'contain',
                            maxWidth: '100%',
                            maxHeight: '100%',
                          }}
                        />
                      </OptionalComponent>
                    </Container>
                    <Text clamp={1} color="var(--gray-100)" className="S2" wordBreak="break-all">
                      {selectedCertificate.settings.icon.split('/').at(-1)}
                    </Text>
                    <Button
                      onClick={() => editImageRef.current?.click()}
                      className="square-32 radius-full padding-0"
                      backgroundColor="var(--gray-600)"
                      hoverBackgroundColor="var(--gray-500)"
                    >
                      <Icon name={ICONS.EDIT} size={12} color={'white'} />
                    </Button>
                    <input
                      style={{ display: 'none' }}
                      ref={editImageRef}
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && file.size <= 10 * 1024 * 1024 && file.type.match(/image\/(png|jpeg|jpg|gif)/)) {
                          handleFile(file);
                        }
                      }}
                    />
                  </Container>
                ) : (
                  <Container
                    background="var(--gray-900)"
                    height="64px"
                    borderRadius="16px"
                    style={{
                      backgroundImage: `url(${generateBorderDashSvg(hex)})`,
                    }}
                  >
                    <UploadLarge
                      placeholder=""
                      wrapperClassNames="height-100-percent"
                      ref={uploadInputRef}
                      CustomTitle={
                        <>
                          <OptionalComponent display={!selectedCertificate.settings.icon}>
                            <Text className="L2 flex ai-center gap-8" color={getThemeColorCSSVariable()}>
                              <Icon name={ICONS.ADD} size={12} />
                              <span>Upload Logo</span>
                            </Text>
                            <Text className="P3" color="var(--gray-400)">
                              Use PNG, JPEG, GIF files to to 10MB size
                            </Text>
                          </OptionalComponent>
                        </>
                      }
                      onFile={handleFile}
                      uploading={uploading}
                      allowedFileTypes={['image/jpg', 'image/jpeg', 'image/png', 'image/gif']}
                    />
                  </Container>
                )}
              </Container>
              <Container className="padding-16 radius-12 flex-column gap-10" background="var(--gray-800)">
                <Text className="S1" color="var(--gray-100)">
                  Design
                </Text>
                <Container
                  background="var(--gray-800)"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                    gap: '8px',
                  }}
                >
                  {certificateDesigns.map((design) => (
                    <Button
                      height="100px"
                      key={design.uuid}
                      className="padding-8"
                      borderRadius="8px"
                      design="LocalBttnSecTransp"
                      border={`2px solid ${selectedCertificateDesign?.id === design.id ? getThemeColorCSSVariable() : 'transparent'}`}
                      onClick={() => {
                        setBlinkCanvas(true);
                        setSelectedCertificateDesign(design);
                        setTimeout(() => setBlinkCanvas(false));
                      }}
                    >
                      <div
                        style={{
                          backgroundImage: `url(${design.thumbnail ?? design.settings.backgroundImage})`,
                          backgroundSize: 'cover',
                        }}
                        className="height-100-percent width-100-percent"
                      />
                    </Button>
                  ))}
                </Container>
              </Container>
            </div>
          )}
        </Container>
        <div className="flex ai-center jc-center flex-1">
          {loading && <WaitingIndicator />}
          {!loading && selectedCertificateDesign && !blinkCanvas && (
            <CanvasComponent
              animate={true}
              certificate={
                selectedCertificateDesign
                  ? GenerateMockCertificate(selectedCertificateDesign, selectedCertificate)
                  : MockCertificate2
              }
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

export default CourseEditCertificateModal;
